import { useState, useEffect } from 'react';
import { FormControl, Grid, TextField, Box, Button, Typography, Card } from '@mui/material';
import intl from 'components/i18n/ReactIntlWrapper';
import ErrorSummary from 'components/common/ErrorSummary';
import CountrySelect from '../common/Selects/CountrySelect';
import { Country } from '../../axiosApi/models/country';
import { useApi } from '../../api/ApiProvider';
import Api from '../../axiosApi/api';
import { useToastMessageQueue } from '../../components/ToastMessages/ToastMessageProvider';
import { useNavigate } from 'react-router';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { PermissionsGate } from 'components/authorization/PermissionsGate';
import { ROLES } from 'common/permissions';
import { ButtonWithOptions } from 'components/common/ButtonWithOptions';
import SaveIcon from '@mui/icons-material/Save';
import { MenuItem } from '@mui/material';

const RegimeNew = () => {
    const navigate = useNavigate();
    const api: Api = useApi();
    const toast = useToastMessageQueue();
    const [countries, setCountries] = useState<Country[]>([]);
    const [errors, setErrors] = useState<any>({});
    const [input, setInput] = useState<any>({});
    const [disabled, setDisabled] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);

    const handleInputChange = (e: any) => {
        setInput({
            ...input,
            [e.target.name]: e.target.value
        });
        setErrors({
            ...errors,
            [e.target.name]: ''
        });
    };

    const fetchCountries = async () => {
        try {
            const response = await api.addressApi.apiVversionAddressCountryAllGet("1");
            if (response.status === 200 && response.data?.data && Array.isArray(response.data.data)) {
                setCountries(response.data.data);
            }
        } catch (error) {
            toast.error({
                header: intl.get("timeOffRegime.toast.error.fetchCountries"),
                body: error.message
            });
        }
    };

    useEffect(() => {
        fetchCountries();
    }, []);

    const handleCountryChange = (country: Country | null) => {
        setInput({
            ...input,
            country: country
        });
        setErrors({
            ...errors,
            country: ''
        });
    };

    const handleSaveClick = () => {
        handleSubmit('save');
      };

      const handleSaveCloseClick = () => {
        handleSubmit('saveAndClose');
      };

    const validateForm = () => {
        if (!input?.name?.trim()) {
            setErrors({
                ...errors,
                name: intl.get("timeOffRegime.form.error.nameRequired")
            });
        }
        if (!input?.country?.id) {
            setErrors({
                ...errors,
                country: intl.get("timeOffRegime.form.error.countryRequired")
            });
        }
        return Object.values(errors).every((err) => err === '');
    };

    const isFormValid = (): boolean => {
        if (!input?.name?.trim()) return false;
        if (!input?.country?.id) return false;
        return true;
      };

    const handleSubmit = async (buttonType: 'save' | 'saveAndClose') => {
        if (!validateForm()) {
          return;
        }
        setLoading(true);
        try {
          const createCommand = {
            name: input?.name?.trim(),
            description: input?.description?.trim() || null,
            idCountry: input?.country?.id || null,
            isActive: true
          };

          const response = await api.timeoffApi.apiVversionTimeOffRegimePost("1", createCommand);

          if (response.status === 200 || response.status === 201) {
            toast.success({
              message: intl.get("timeOffRegime.toast.success.create")
            });
            if (buttonType === 'save') {
                navigate(`/regime/${response.data.data.id}/types`);
            } else {
                navigate('/regime');
            }
          } else {
            toast.error({
              header: intl.get("timeOffRegime.toast.error.create")
            });
          }
        } catch (error) {
          toast.error({
            header: intl.get("timeOffRegime.toast.error.create"),
            body: error.message
          });
        } finally {
          setLoading(false);
        }
      };

    return (
        <Box component="form" className='my-3' sx={{width: '100%', p: 2}}>
            <Card sx={{ p: 3 }}>
            <Typography color="primary" variant="h2" component="h2" sx={{ mb: 2 }}>
                {intl.get("timeOffRegime.form.title")}
            </Typography>
            <Grid container>
                <Grid size={12}>
                    <ErrorSummary errors={errors} showEverything={true} />
                </Grid>
            </Grid>
            <Grid container spacing={2} sx={{ width: '100%', margin: '0 auto' }}>
                <Grid size={12}>
                    <Box display="flex" gap={2} flexDirection="column">
                        <FormControl error={Boolean(errors?.country)}>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                * {intl.get("timeOffRegime.form.country")}
                            </Typography>
                            {errors?.country && (
                                <Typography variant="caption" color="error" sx={{ mb: 1 }}>
                                    {errors?.country}
                                </Typography>
                            )}
                            <CountrySelect
                                handleCountryChange={handleCountryChange}
                                selectedCountry={input?.country?.labelKey || null}
                                countries={countries}
                                inputCountry={input?.country?.name || ''}
                                isClearable={true}
                            />
                        </FormControl>

                        <FormControl>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                * {intl.get("timeOffRegime.form.name")}
                            </Typography>
                            <TextField
                                id="regimeName"
                                name="name"
                                label={intl.get('timeOffRegime.form.name')}
                                placeholder={intl.get('timeOffRegime.form.placeholder.name')}
                                value={input?.name || ''}
                                onChange={handleInputChange}
                                disabled={disabled}
                                error={!!errors.name}
                                helperText={errors.name}
                                fullWidth
                                slotProps={{
                                    input: {
                                      inputProps: { maxLength: 40 },
                                    }
                                  }}
                            />
                        </FormControl>

                        <FormControl>
                            <TextField
                                id="regimeDescription"
                                name="description"
                                label={intl.get('timeOffRegime.form.description')}
                                placeholder={intl.get('timeOffRegime.form.placeholder.description')}
                                value={input?.description || ''}
                                onChange={handleInputChange}
                                disabled={disabled}
                                error={!!errors.description}
                                helperText={errors.description}
                                multiline
                                rows={3}
                                fullWidth
                                slotProps={{
                                    input: {
                                      inputProps: { maxLength: 255 },
                                    }
                                }}
                            />
                        </FormControl>

                        <Box display="flex" gap={2} justifyContent="flex-end" sx={{ mt: 2 }}>
                            <Button
                                onClick={() => navigate('/regime')}
                                color="secondary"
                                variant="outlined"
                                disabled={disabled}
                            >
                                <ArrowBackIcon />
                            </Button>
                            <PermissionsGate
                                identifier="ui.timeoff.regime-new2.gate"
                                editRoles={[ROLES.employeeWriter]}
                                viewRoles={[ROLES.employeeReader]}
                            >
                                <ButtonWithOptions
                                    color="primary"
                                    variant="contained"
                                    disabled={!isFormValid()}
                                    menu={
                                        <>
                                        <MenuItem
                                            onClick={handleSaveCloseClick}
                                            disabled={!isFormValid()}
                                        >
                                            {intl.get('save&close.button')}
                                        </MenuItem>
                                        </>
                                    }
                                        onClick={handleSaveClick}
                                    >
                                    <SaveIcon className="mb-1" /> {intl.get('save.button')}
                                </ButtonWithOptions>
                            </PermissionsGate>
                        </Box>
                    </Box>
                </Grid>
            </Grid>
            </Card>
        </Box>
    )
}

export default RegimeNew;
